import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/client.js";
import { useAppActions } from "../app/AppProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import type { Funnel, Lead, Stage, WhatsappChat } from "../types.ts";
import { displayNameForLeadPayload, resolveChatLeadForStager, shouldLinkChatToLead } from "./ChatLeadStager.helpers.ts";

/**
 * Pill/dropdown no header do ChatRoom que mostra ou cria a posicao do contato
 * no kanban. Resolve UX de "preciso ir ate o kanban pra mover esse lead".
 *
 * Estados:
 * - Lead existe + esta no state.leads: mostra "{funil} > {stage}" como pill clicavel
 * - Lead nao existe: mostra "+ Adicionar ao kanban" como CTA
 * - Click abre dropdown com TODOS os funis + stages de cada um
 * - Selecionar: upsert via /leads/upsert (cria ou atualiza), broadcast lead.upsert via WS
 */

type Props = {
  chat: WhatsappChat;
  display: string;
  lead?: Lead | null;
};

export function ChatLeadStager({ chat, display, lead: resolvedLead = null }: Props) {
  const { state, dispatch } = useAppActions();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [selectedFunnelId, setSelectedFunnelId] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const lead: Lead | null = resolveChatLeadForStager(chat, resolvedLead, state.leads);
  const funnel: Funnel | null = lead?.funnelId
    ? state.funnels.find((f) => f.id === lead.funnelId) || null
    : null;
  const stage: Stage | null = funnel && lead?.stage
    ? funnel.stages.find((s) => s.id === lead.stage) || null
    : null;

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSelectedFunnelId("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!state.funnels.length) return null;

  async function moveOrCreate(funnelId: string, stageId: string) {
    setOpen(false);
    setSelectedFunnelId("");
    const isNew = !lead;
    // Gera id estavel pra novos leads (mesmo padrao usado em outros pontos do CRM)
    const id = lead?.id || (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `lead-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
    const payload = {
      id,
      name: displayNameForLeadPayload(lead, display),
      phone: lead?.phone || chat.phone || "",
      email: lead?.email || "",
      course: lead?.course || "",
      value: lead?.value ?? 0,
      priority: lead?.priority || "Media",
      notes: lead?.notes || "",
      funnelId,
      stage: stageId,
      source: lead?.source || (isNew ? "WhatsApp" : undefined),
    };
    // Update otimista
    const next: Lead = { ...(lead || {}), ...payload };
    dispatch({ type: "UPSERT_LEAD", lead: next });
    if (shouldLinkChatToLead(chat, next)) {
      dispatch({ type: "PATCH_CHAT", chat: { chatJid: chat.chatJid, leadId: next.id } });
    }
    try {
      const saved = await apiFetch("/leads/upsert", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const savedLead = (saved as Lead) || next;
      dispatch({ type: "UPSERT_LEAD", lead: savedLead });
      // Linka o chat ao lead quando ele veio por telefone, mas ainda sem leadId.
      if (shouldLinkChatToLead(chat, savedLead)) {
        try {
          await apiFetch(`/whatsapp/chats/${encodeURIComponent(chat.chatJid)}/lead`, {
            method: "PATCH",
            body: JSON.stringify({ leadId: String(savedLead.id) }),
          });
          dispatch({ type: "PATCH_CHAT", chat: { chatJid: chat.chatJid, leadId: savedLead.id } });
        } catch (err) {
          // Se o endpoint nao existir ainda, ignora — webhook auto-vincula via phone
          console.warn("[wcrm] link chat-lead failed", err);
        }
      }
      const verb = isNew ? "Lead adicionado em" : "Movido para";
      const fName = state.funnels.find((f) => f.id === funnelId)?.title || funnelId;
      const sName = state.funnels.find((f) => f.id === funnelId)?.stages.find((s) => s.id === stageId)?.title || stageId;
      showToast(`${verb} ${fName} › ${sName}`, "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Falha ao salvar no kanban", "error");
    }
  }

  // Pill compacto que mostra estado atual ou CTA
  const label = lead && funnel && stage
    ? `${funnel.title} › ${stage.title}`
    : lead && funnel
      ? funnel.title
      : "+ Adicionar ao kanban";
  const selectedMenuFunnel = selectedFunnelId
    ? state.funnels.find((item) => item.id === selectedFunnelId) || null
    : null;

  function toggleMenu() {
    const nextOpen = !open;
    setOpen(nextOpen);
    setSelectedFunnelId("");
  }

  return (
    <div className="chat-stager" ref={containerRef}>
      <button
        type="button"
        className={`chat-stager-pill ${lead ? "is-set" : "is-empty"} ${open ? "is-open" : ""}`}
        onClick={toggleMenu}
        style={stage ? { background: stage.color, color: pillTextColor(stage.color) } : undefined}
        title={lead ? "Mover para outra etapa" : "Adicionar este contato ao kanban"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="chat-stager-label">{label}</span>
        <span className="chat-stager-caret">▾</span>
      </button>
      {open ? (
        <div className="chat-stager-menu" role="menu" aria-label="Mover lead para etapa">
          {selectedMenuFunnel ? (
            <div className="chat-stager-panel">
              <button
                type="button"
                className="chat-stager-back"
                onClick={() => setSelectedFunnelId("")}
              >
                <span className="chat-stager-back-icon">â€¹</span>
                <span className="chat-stager-back-text">{selectedMenuFunnel.title}</span>
              </button>
              <div className="chat-stager-stages">
                {selectedMenuFunnel.stages.length ? selectedMenuFunnel.stages.map((s) => {
                  const active = lead?.funnelId === selectedMenuFunnel.id && lead?.stage === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`chat-stager-stage ${active ? "is-active" : ""}`}
                      style={{ borderLeftColor: s.color }}
                      title={s.title}
                      role="menuitem"
                      aria-current={active ? "true" : undefined}
                      onClick={() => void moveOrCreate(selectedMenuFunnel.id, s.id)}
                    >
                      <span className="chat-stager-stage-dot" style={{ background: s.color }} />
                      <span className="chat-stager-stage-name">{s.title}</span>
                      {active ? <span className="chat-stager-check">✓</span> : null}
                    </button>
                  );
                }) : (
                  <div className="chat-stager-empty">Nenhuma etapa neste funil.</div>
                )}
              </div>
            </div>
          ) : (
            <div className="chat-stager-funnels">
              <div className="chat-stager-funnel-title">Selecione o funil</div>
              {state.funnels.map((f) => {
                const active = lead?.funnelId === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    className={`chat-stager-funnel-option ${active ? "is-active" : ""}`}
                    role="menuitem"
                    onClick={() => setSelectedFunnelId(f.id)}
                    title={f.title}
                  >
                    <span className="chat-stager-funnel-name">{f.title}</span>
                    <span className="chat-stager-funnel-meta">{f.stages.length} etapa{f.stages.length === 1 ? "" : "s"}</span>
                    <span className="chat-stager-next">â€º</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function pillTextColor(bgHex: string): string {
  const match = bgHex.match(/^#([0-9a-f]{6})$/i);
  if (!match) return "#fff";
  const n = parseInt(match[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#111b21" : "#ffffff";
}
